import { fetchCorporationSettingAPI, updateCorporationSettingAPI } from '@/api/admin/setting';
import BaseDialog from '@/components/ui/BaseDialog';
import BasePaper from '@/components/ui/BasePaper';
import BaseTitle from '@/components/ui/BaseTitle';
import StoreSettingsForm from '@/pages/StoreMgmt/settings/StoreSettingsForm';
import { useAppContext } from '@/provider/contexts';
import {
    CorporateSettingInfo,
    CorporateSettingPayload,
    CorporationAction,
    CorporationState,
    FieldOption,
} from '@/types/pages/admin/settings';
import { Settings } from '@mui/icons-material';
import { Box, Checkbox, CircularProgress, FormControlLabel, Typography } from '@mui/material';
import localforage from 'localforage';
import { join, orderBy } from 'lodash';
import { useCallback, useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import type { FieldOption as StoreFieldOption } from '@/types/pages/storeManagement/settings';

const reducer = (state: CorporationState, action: CorporationAction) => {
    switch (action.type) {
        case 'SET_LOADING':
            return { ...state, isLoading: action.payload };
        case 'TOGGLE_DIALOG':
            return { ...state, dialogOpen: action.payload };
        case 'SET_INIT':
            return { ...state, initSettings: action.payload };
        case 'SET_ORIGINAL_INIT':
            return { ...state, originalSettings: action.payload };
        case 'SET_FORCE_TO_STORE':
            return { ...state, forceToStore: action.payload };
        case 'SET_VALIDATION_ERRORS':
            return { ...state, validationErrors: action.payload };
        case 'SET_CUSTOM_FIELD_OPTION':
            return { ...state, customFieldOptions: orderBy(action.payload, ['name', 'key']) };
        default:
            return state;
    }
};

const defaultState: CorporationState = {
    isLoading: true,
    initSettings: {},
    originalSettings: {},
    customFieldOptions: [],
    validationErrors: [],
    dialogOpen: false,
    forceToStore: false,
};

const AdminSettingPage = () => {
    const [
        { isLoading, initSettings, originalSettings, validationErrors, dialogOpen, forceToStore, customFieldOptions },
        dispatch,
    ] = useReducer(reducer, defaultState);
    const { t } = useTranslation();
    const { setNotification } = useAppContext();
    const storeCustomFieldOptions: StoreFieldOption[] = useMemo(
        () =>
            customFieldOptions.map((o: FieldOption) => ({
                fieldnum: Number(o.fieldnum ?? o.id),
                key: String(o.name ?? o.key),
                name: String(o.name ?? o.key),
            })),
        [customFieldOptions],
    );

    const fetchCorporationSetting = useCallback(async () => {
        dispatch({ type: 'SET_LOADING', payload: true });

        try {
            const corporationId = await localforage.getItem<number>('corporation_id');
            if (!corporationId) {
                console.warn(t('No corporation_id found in local storage.'));
                return;
            }
            const {
                data: { data, isSuccess, message },
            } = await fetchCorporationSettingAPI(corporationId);
            if (isSuccess) {
                const setting = {
                    ...data,
                    pp2w_trigger_fields: data.pp2w_trigger_fields
                        ? data.pp2w_trigger_fields.split('|').map((o) => Number(o))
                        : [],
                };
                dispatch({ type: 'SET_INIT', payload: setting });
                dispatch({ type: 'SET_ORIGINAL_INIT', payload: setting });
                dispatch({ type: 'SET_CUSTOM_FIELD_OPTION', payload: data.layout_data_fields ?? [] });
            } else {
                setNotification({ message: message || t('Failed to save settings'), severity: 'error' });
            }
        } catch (err: unknown) {
            if (err instanceof Error) {
                setNotification({ message: err?.message || t('Failed to load settings'), severity: 'error' });

                return;
            }
            console.error(err);
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, [setNotification, t]);

    useEffect(() => {
        fetchCorporationSetting();
    }, [fetchCorporationSetting]);

    const handleOnSubmit = async () => {
        dispatch({ type: 'TOGGLE_DIALOG', payload: false });
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const corporationId = await localforage.getItem<number>('corporation_id');
            if (!corporationId) {
                console.warn(t('No corporation_id found in local storage.'));
                return;
            }

            const diff: Partial<
                CorporateSettingInfo & {
                    pp2w_trigger_fields?: number[];
                }
            > = Object.fromEntries(
                (Object.keys(initSettings) as Array<keyof CorporateSettingInfo>)
                    .filter((k) => initSettings[k] !== originalSettings[k])
                    .map((k) => [k, initSettings[k]]),
            );

            const payload: CorporateSettingPayload = {
                ...diff,
                pp2w_trigger_fields: join(diff.pp2w_trigger_fields, '|'),
                z_force_to_all_stores: forceToStore,
            };
            if (!diff.pp2w_trigger_fields) {
                delete payload['pp2w_trigger_fields'];
            }
            const {
                data: { data, isSuccess, message },
            } = await updateCorporationSettingAPI(corporationId, payload);

            if (!isSuccess) {
                dispatch({ type: 'SET_VALIDATION_ERRORS', payload: data.exception_settings || [] });
                setNotification({ message: message || t('Failed to save settings'), severity: 'error' });
            } else {
                setNotification({ message: t('Settings saved successfully!'), severity: 'success' });
                dispatch({ type: 'SET_FORCE_TO_STORE', payload: false });
                await fetchCorporationSetting();
            }
        } catch (err: unknown) {
            if (err instanceof Error) {
                setNotification({ message: t('Settings saved successfully!'), severity: 'success' });
                return;
            }
            console.error('Error saving settings:', err);
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    return (
        <>
            <BasePaper isFullHeight={true}>
                <BaseTitle icon={<Settings />} title={t('Corporation Settings')} />
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress size={40} />
                    </Box>
                ) : (
                    <StoreSettingsForm
                        hideCancelBtn
                        isCorporation
                        customFieldOptions={storeCustomFieldOptions}
                        settings={initSettings}
                        originalSettings={originalSettings}
                        validatedResultList={validationErrors}
                        setSettings={(payload) => dispatch({ type: 'SET_INIT', payload })}
                        onSubmit={() => dispatch({ type: 'TOGGLE_DIALOG', payload: true })}
                        isLoading={isLoading}
                    />
                )}
            </BasePaper>
            <BaseDialog
                open={dialogOpen}
                dialogTitle={t('Corporation Settings')}
                confirmButtonText={t('Save')}
                cancelButtonText={t('Cancel')}
                onConfirm={handleOnSubmit}
                onClose={() => dispatch({ type: 'TOGGLE_DIALOG', payload: false })}
                TransitionProps={{
                    onExited: () => dispatch({ type: 'SET_FORCE_TO_STORE', payload: false }),
                }}
            >
                <FormControlLabel
                    sx={{
                        alignItems: 'flex-start',
                        gap: 1,
                        '& .MuiFormControlLabel-label': { mt: 0.25 },
                    }}
                    control={
                        <Checkbox
                            checked={forceToStore}
                            onChange={(_, checked) => dispatch({ type: 'SET_FORCE_TO_STORE', payload: checked })}
                        />
                    }
                    label={
                        <Box>
                            <Typography variant="body1">{t('Force apply to all stores')}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('If checked, this change will be applied to all stores in the corporation.')}
                            </Typography>
                        </Box>
                    }
                />
            </BaseDialog>
        </>
    );
};

export default AdminSettingPage;
